// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Trend signal summary, status, narrative-text, and story-card builders
// extracted from app.js. This is the layer between the raw trend rows
// (built by trend-row-builder) and the rendered trend-history UI:
//
//   rows
//     -> buildTrendSignalSummary(rows, definition)   // per-signal stats
//     -> buildTrendSignalSummaries(rows)             // ranked list
//     -> buildTrendOwnerStory(allRows, usableRows)   // top-card story
//
// It depends on a handful of definitions, getters, and helpers that
// remain in app.js, so it is exposed as a factory:
//
//   const helpers = createTrendSignalSummary({ ...deps });
//
// app.js destructures the returned helpers and assigns them under the
// same names the rest of the app expects.

import { averageValues } from "../core/math.js";
import { escapeHtml } from "../core/html.js";
import { buildTrendComparison, buildTrendSignalEvidence } from "./trend-comparison.js";

export function overallTrendVerdict(rows, signals = []) {
  const latest = rows.at(-1)?.overallScore;
  const recent = rows.slice(-10);
  const previous = rows.at(-1)?.operatingContext
    ? buildTrendComparison(rows, "overallScore").referenceAverage
    : averageValues(rows.slice(-9, -1).map((row) => row.overallScore).filter(Number.isFinite));
  const repeated = recent.filter((row) => Number.isFinite(row.overallScore) && row.overallScore < 88).length >= 2;
  let streak = 0;
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    if (!Number.isFinite(rows[index].overallScore) || rows[index].overallScore >= 72) break;
    streak += 1;
  }
  if (signals.some((signal) => signal.status?.className === "red")
    || (Number.isFinite(latest) && ((repeated && Number.isFinite(previous) && latest <= previous - 8 && latest < 88) || streak >= 3))) {
    return { label: "Attention", className: "red" };
  }
  if (signals.some((signal) => signal.status?.className === "amber" && signal.status?.label !== "Needs profile") || (Number.isFinite(latest) && latest < 88)) {
    return { label: "Watch", className: "amber" };
  }
  if (!Number.isFinite(latest)) return { label: "No data", className: "muted" };
  return { label: "Stable", className: "green" };
}

export function createTrendSignalSummary({
  getTrendSignalDefinitions,
  trendMetricAvailable,
  trendAnchorIndex,
  trendWindowRows,
  reviewMonitorEnabled,
  reviewMonitorCapabilityAvailable = () => true,
  selectedTrendAircraft,
  getActiveTrendMetric,
  trendMetricMeta,
  chtCautionThresholdF,
  chtHealthReviewThresholdF,
  chtCoolingRateTriggerFpm,
  trendCylinderLabel,
  trendTurboPrimaryReason,
  trendTurboPeakSummary,
  trendDivergencePrimaryRows,
  trendDivergenceRepeatedCylinders,
  trendDivergenceLiftSummary,
}) {
  function trendHealthKeyForSignalKey(key) {
    return {
      sensorScore: "sensors",
      turboScore: "turbo",
      oilSystemScore: "oilSystem",
      chtScore: "cht",
      egtScore: "egt",
      valveScore: "valve",
      combustionScore: "combustion",
      inductionLeakScore: "inductionLeak",
      magScore: "mag",
      carbIceScore: "carbIce",
      coolingScore: "cooling",
      coolingRateScore: "coolingRate",
      chargingScore: "charging",
      electricalScore: "electrical",
    }[key] ?? null;
  }

  function trendReviewMonitorEnabledForSignalKey(key, aircraft = selectedTrendAircraft()) {
    const monitorKey = trendHealthKeyForSignalKey(key);
    return !monitorKey
      || (reviewMonitorEnabled(monitorKey, aircraft) && reviewMonitorCapabilityAvailable(monitorKey, aircraft) !== false);
  }

  function trendSignalDefinitionsForRows(rows, aircraft = selectedTrendAircraft()) {
    return getTrendSignalDefinitions()
      .filter((definition) => trendReviewMonitorEnabledForSignalKey(definition.key, aircraft))
      .filter((definition) => trendMetricAvailable(rows, definition.key));
  }

  function trendDominantAffected(rows, definition) {
    const counts = new Map();
    rows.forEach((row) => {
      const label = definition.affected?.(row);
      if (!label) return;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? "";
  }

  function trendAverageScore(rows, key) {
    return averageValues(rows.map((row) => row[key]).filter(Number.isFinite));
  }

  function trendConsecutiveFlagCount(rows, key, anchorIndex = rows.length - 1) {
    let count = 0;
    for (let index = Math.min(anchorIndex, rows.length - 1); index >= 0; index -= 1) {
      const value = rows[index]?.[key];
      if (!Number.isFinite(value) || value >= 88) break;
      count += 1;
    }
    return count;
  }

  // Consecutive deep-low (<72) rows counting back from the anchor flight. This is the
  // "it has been genuinely low for the last several flights" gate that can earn Attention
  // even when the direction delta is flat (a plateau of bad never reads as "worsening").
  function trendConsecutiveRedCount(rows, key, anchorIndex = rows.length - 1) {
    let count = 0;
    for (let index = Math.min(anchorIndex, rows.length - 1); index >= 0; index -= 1) {
      const value = rows[index]?.[key];
      if (!Number.isFinite(value) || value >= 72) break;
      count += 1;
    }
    return count;
  }

  function trendRowFlagged(row, definition) {
    if (typeof definition?.flagged === "function") return definition.flagged(row) === true;
    const value = row?.[definition?.key];
    return Number.isFinite(value) && value < 88;
  }

  function trendConsecutiveFlaggedRows(rows, definition, anchorIndex = rows.length - 1) {
    let count = 0;
    for (let index = Math.min(anchorIndex, rows.length - 1); index >= 0; index -= 1) {
      if (!Number.isFinite(rows[index]?.[definition.key]) || !trendRowFlagged(rows[index], definition)) break;
      count += 1;
    }
    return count;
  }

  function trendDirection(latestScore, previousAverage) {
    if (!Number.isFinite(latestScore) || !Number.isFinite(previousAverage)) return "steady";
    const delta = latestScore - previousAverage;
    if (delta <= -8) return "worse";
    if (delta >= 8) return "better";
    return "steady";
  }

  function trendDirectionText(direction) {
    if (direction === "unknown") return "not enough comparable evidence";
    if (direction === "worse") return "worse recently";
    if (direction === "better") return "improving recently";
    return "holding steady, not worsening";
  }

  // Severity ladder (alarm redesign): Stable (green) / Watch (amber) / Attention (red),
  // with "No data" muted handled by callers. Red must be earned — a cue goes Attention
  // only when it is BOTH repeating and trending worse, or when the most recent flights
  // hold a sustained deep-low streak. A chronic-but-steady cue stays Watch indefinitely:
  // a known pattern is information, not an alarm. A good latest score reads Stable even
  // with flagged history; the story layer adds recovered phrasing via signal.recovered.
  function trendSignalStatus(signal) {
    if (!Number.isFinite(signal.latestScore)) return { label: "No data", className: "muted" };
    const latestLow = Number.isFinite(signal.latestScore) && signal.latestScore < 72;
    const latestGood = Number.isFinite(signal.latestScore) && signal.latestScore >= 88;
    const repeated = signal.recentFlagged >= 2 || signal.consecutive >= 2;
    const worsening = signal.direction === "worse";
    const deepLowStreak = (signal.redStreak ?? 0) >= 3;
    if (signal.key === "inductionLeakScore") {
      // Induction cues are detected events, not score dips: repeated events stay Watch
      // even when the score recovers, so an intermittent leak cannot hide as Stable.
      if ((repeated && worsening) || deepLowStreak) return { label: "Attention", className: "red" };
      if (signal.flaggedCount > 0 || latestLow) return { label: "Watch", className: "amber" };
      return latestGood ? { label: "Stable", className: "green" } : { label: "Watch", className: "amber" };
    }
    if (signal.key === "combustionScore" || signal.key === "oilSystemScore") {
      // Metrics whose Watch band intentionally hosts capped advisories: CHT/EGT
      // pairing floors transient cues at 74, and the oil warm-up advisory
      // ("try to operate at 180 F+") caps at 76 by design. For these, red must
      // come from deep (<72) evidence repeating — genuine excursions like an
      // oil-temp exceedance or a systemic pairing split score deep and keep
      // their red path, while chronic advisory-band flights can read "worse"
      // against a cleaner average without that being an alarm.
      const deepRepeated = (signal.redRecent ?? 0) >= 2;
      if (latestGood) return { label: "Stable", className: "green" };
      if ((deepRepeated && worsening) || deepLowStreak) return { label: "Attention", className: "red" };
      return { label: "Watch", className: "amber" };
    }
    if (latestGood) return { label: "Stable", className: "green" };
    if ((repeated && worsening) || deepLowStreak) return { label: "Attention", className: "red" };
    return { label: "Watch", className: "amber" };
  }

  function buildTrendSignalSummary(rows, definition) {
    const anchorIndex = trendAnchorIndex(rows);
    const historyRows = rows.slice(0, anchorIndex + 1);
    const metricRows = historyRows.filter((row) => Number.isFinite(row[definition.key]));
    if (!metricRows.length) {
      if (definition.key === "turboScore"
        && rows.some((row) => row.turboAvailable === true || Boolean(row.turboStatus) || Number.isFinite(row.turboScore) || Number(row.turboLimitCount) > 0)) {
        return {
          ...definition,
          average: null,
          affected: "",
          consecutive: 0,
          direction: "steady",
          flaggedCount: 0,
          flaggedRows: [],
          latestScore: null,
          metricRows: 0,
          minRecent: null,
          recentCount: 0,
          recentFlagged: 0,
          recentFlaggedRows: [],
          redRecent: 0,
          sortWeight: 0,
          status: { label: "Needs profile", className: "amber" },
        };
      }
      return null;
    }
    const recentRows = historyRows.slice(-10);
    const flaggedRows = metricRows.filter((row) => trendRowFlagged(row, definition));
    const recentFlaggedRows = recentRows.filter((row) => trendRowFlagged(row, definition));
    const previousRows = historyRows.slice(Math.max(0, anchorIndex - 8), Math.max(0, anchorIndex));
    const latest = historyRows.at(-1);
    const latestScore = Number.isFinite(latest?.[definition.key]) ? latest[definition.key] : null;
    const comparison = buildTrendComparison(historyRows, definition.key);
    const previousAverage = latest?.operatingContext ? comparison.referenceAverage : trendAverageScore(previousRows, definition.key);
    const minRecent = Math.min(...recentRows.map((row) => row[definition.key]).filter(Number.isFinite));
    const redRecent = recentRows.filter((row) => Number.isFinite(row[definition.key]) && row[definition.key] < 72).length;
    const consecutive = typeof definition.flagged === "function"
      ? trendConsecutiveFlaggedRows(historyRows, definition, anchorIndex)
      : trendConsecutiveFlagCount(historyRows, definition.key, anchorIndex);
    const direction = latest?.operatingContext && previousAverage === null ? "unknown" : trendDirection(latestScore, previousAverage);
    const redStreak = trendConsecutiveRedCount(historyRows, definition.key, anchorIndex);
    const affected = trendDominantAffected(recentFlaggedRows.length ? recentFlaggedRows : flaggedRows, definition);
    const scorePressure = Number.isFinite(latestScore) ? Math.max(0, 88 - latestScore) : 0;
    const sortWeight = recentFlaggedRows.length * 60
      + flaggedRows.length * 18
      + consecutive * 24
      + redRecent * 34
      + scorePressure * 2
      + (direction === "worse" ? 14 : 0)
      - (Number.isFinite(latestScore) && latestScore >= 88 ? 34 : 0);
    const repeated = recentFlaggedRows.length >= 2 || consecutive >= 2;
    const recovered = Number.isFinite(latestScore) && latestScore >= 88
      && flaggedRows.length > 0
      && (repeated || flaggedRows.length >= 3 || redRecent >= 2);
    const status = trendSignalStatus({
      latestScore,
      key: definition.key,
      recentFlagged: recentFlaggedRows.length,
      redRecent,
      redStreak,
      consecutive,
      direction,
      flaggedCount: flaggedRows.length,
    });
    return {
      ...definition,
      average: trendAverageScore(metricRows, definition.key),
      affected,
      consecutive,
      direction,
      flaggedCount: flaggedRows.length,
      flaggedRows,
      latestScore,
      metricRows: metricRows.length,
      minRecent: Number.isFinite(minRecent) ? minRecent : null,
      recentCount: recentRows.length,
      recentFlagged: recentFlaggedRows.length,
      recentFlaggedRows,
      recovered,
      redRecent,
      redStreak,
      sortWeight,
      status,
      evidence: buildTrendSignalEvidence(historyRows, definition.key, status.className, direction),
    };
  }

  // Every tracked area that produces a usable summary (one tile in the rail /
  // one entry in the Status mix). A definition that is available but scores no
  // usable rows (e.g. turbo shown as a "Limited" card) yields null and drops out,
  // so this is the count the mix panel and the verdict ribbon must both agree on.
  function buildAllTrendSignals(rows) {
    return trendSignalDefinitionsForRows(rows)
      .map((definition) => buildTrendSignalSummary(rows, definition))
      .filter(Boolean);
  }

  function buildTrendSignalSummaries(rows) {
    return buildAllTrendSignals(rows)
      .filter((signal) => signal.flaggedCount > 0 || (Number.isFinite(signal.latestScore) && signal.latestScore < 88))
      .sort((a, b) => b.sortWeight - a.sortWeight || (a.latestScore ?? 100) - (b.latestScore ?? 100));
  }

  function trendCoverageLabel(allRows, usableRows) {
    const errorCount = allRows.filter((row) => row.error).length;
    if (!usableRows.length) return errorCount ? "No usable flights" : "No flights";
    if (usableRows.length === 1) return "One flight";
    if (usableRows.length < 5) return "Limited history";
    if (errorCount) return `${usableRows.length} reviewed, ${errorCount} skipped`;
    return "Good history";
  }

  function selectedTrendSignal(rows) {
    const definition = trendSignalDefinitionsForRows(rows).find((item) => item.key === trendStoryMetricKey(getActiveTrendMetric()));
    return definition ? buildTrendSignalSummary(rows, definition) : null;
  }

  function trendStoryMetricKey(metricKey) {
    return {
      peakCht: "chtScore",
      maxTit: "turboScore",
      titP95: "turboScore",
      maxMap: "turboScore",
      coolingRateFpm: "coolingRateScore",
      egtSpreadP90: "egtScore",
      busRange: "electricalScore",
    }[metricKey] ?? metricKey;
  }

  function trendSignalRepeatText(signal) {
    if (!signal.flaggedCount) return `Selected flights do not show a repeating ${signal.label.toLowerCase()} item`;
    if (signal.recentFlagged) {
      return `${signal.recentFlagged} of ${signal.recentCount} selected flight${signal.recentCount === 1 ? "" : "s"}`;
    }
    return `${signal.flaggedCount} selected flight${signal.flaggedCount === 1 ? "" : "s"}`;
  }

  function trendAffectedRows(signal) {
    return signal?.recentFlaggedRows?.length ? signal.recentFlaggedRows : signal?.flaggedRows ?? [];
  }

  function trendDominantNumber(rows, key) {
    const counts = new Map();
    rows.forEach((row) => {
      const value = row?.[key];
      if (!Number.isFinite(value)) return;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0] ?? null;
  }

  function trendCylinderPeakCht(row, cylinder) {
    return row?.chtByCylinder?.find((item) => item.cylinder === cylinder)?.peak ?? null;
  }

  function trendChtThresholdSummary(rows, cylinder, threshold = chtCautionThresholdF) {
    const peaks = rows
      .map((row) => trendCylinderPeakCht(row, cylinder))
      .filter(Number.isFinite);
    if (!peaks.length) return null;
    const above = peaks.filter((value) => value >= threshold).length;
    const highest = Math.max(...peaks);
    return { above, highest, threshold, total: peaks.length };
  }

  function trendValveSpecificEvidenceText(rows) {
    const cylinder = trendDominantNumber(rows, "valveCylinder");
    const candidates = rows
      .filter((row) => row.valveCylinder === cylinder && Number.isFinite(row.valveAmplitude))
      .sort((a, b) => (a.valveScore ?? 100) - (b.valveScore ?? 100) || (b.valveAmplitude ?? 0) - (a.valveAmplitude ?? 0));
    const row = candidates[0];
    if (!Number.isFinite(cylinder) || !row) return "";
    const amplitude = Math.round(row.valveAmplitude);
    const frequency = Number.isFinite(row.valveFrequencyCpm)
      ? ` at ${row.valveFrequencyCpm.toFixed(1)} cycles/min`
      : "";
    const ratio = Number.isFinite(row.valveRatio)
      ? `, about ${row.valveRatio.toFixed(1)}x the peer-cylinder movement`
      : "";
    const windowCount = Number.isFinite(row.valveFlaggedWindows) && row.valveFlaggedWindows > 1
      ? ` across ${Math.round(row.valveFlaggedWindows)} stable windows`
      : "";
    return `${trendCylinderLabel(cylinder, rows)} EGT showed about ${amplitude} F peak-to-peak rhythm${frequency}${ratio}${windowCount}.`;
  }

  function trendSpecificEvidenceText(signal) {
    const rows = trendAffectedRows(signal);
    if (!rows.length) return "";
    if (signal.key === "valveScore") return trendValveSpecificEvidenceText(rows);
    if (signal.key === "gamiSweepScore") {
      const spreads = rows.map((row) => row.gamiSweepSpreadGph).filter(Number.isFinite);
      if (!spreads.length) return "";
      const latest = rows.slice().reverse().find((row) => Number.isFinite(row.gamiSweepSpreadGph));
      const highest = Math.max(...spreads);
      const confidence = latest?.gamiSweepConfidence ? ` with ${String(latest.gamiSweepConfidence).toLowerCase()} confidence` : "";
      const peaks = Number.isFinite(latest?.gamiSweepClearPeakCount) && Number.isFinite(latest?.gamiSweepPeakCount)
        ? ` and ${latest.gamiSweepClearPeakCount}/${latest.gamiSweepPeakCount} clear peaks`
        : "";
      const multiSweep = rows.some((row) => Number(row.gamiSweepCount) > 1) ? " (widest sweep per flight)" : "";
      const engine = latest?.gamiSweepEngineLabel ? ` on the ${String(latest.gamiSweepEngineLabel).toLowerCase()}` : "";
      return `Saved GAMI sweeps reached up to ${highest.toFixed(2)} GPH${multiSweep}. The most recent flagged flight's widest sweep was ${Number(latest.gamiSweepSpreadGph).toFixed(2)} GPH${engine}${confidence}${peaks}.`;
    }
    if (signal.key === "coolingScore") {
      const cylinder = trendDominantNumber(rows, "coolingCylinder");
      const summary = trendChtThresholdSummary(rows, cylinder);
      if (!Number.isFinite(cylinder) || !summary) return "";
      const base = `${trendCylinderLabel(cylinder, rows)} ran warmer than the other cylinders in the flagged flights.`;
      if (summary.above) {
        return `${base} Its CHT peak exceeded ${Math.round(summary.threshold)} F in ${summary.above} of those ${summary.total} flight${summary.total === 1 ? "" : "s"}; the highest peak was ${Math.round(summary.highest)} F.`;
      }
      return `${base} Its highest CHT peak was ${Math.round(summary.highest)} F, so the repeat is mainly a warmer-than-peers pattern rather than a repeated ${Math.round(summary.threshold)} F exceedance.`;
    }
    if (signal.key === "chtScore") {
      const cylinder = trendDominantNumber(rows, "hottestCylinder");
      const summary = trendChtThresholdSummary(rows, cylinder);
      if (!Number.isFinite(cylinder) || !summary) return "";
      if (summary.above) {
        return `${trendCylinderLabel(cylinder, rows)} CHT peak exceeded ${Math.round(summary.threshold)} F in ${summary.above} of the flagged flight${summary.above === 1 ? "" : "s"}; the highest peak was ${Math.round(summary.highest)} F.`;
      }
      return `${trendCylinderLabel(cylinder, rows)} was the warmest cylinder in the flagged flights, with a highest CHT peak of ${Math.round(summary.highest)} F.`;
    }
    if (signal.key === "coolingRateScore") {
      const cylinder = trendDominantNumber(rows, "coolingRateCylinder");
      const rates = rows.map((row) => row.coolingRateFpm).filter(Number.isFinite);
      if (!Number.isFinite(cylinder) || !rates.length) return "";
      const thresholds = rows.map((row) => row.coolingRateThresholdFpm).filter(Number.isFinite);
      const threshold = thresholds.length ? Math.round(thresholds[thresholds.length - 1]) : chtCoolingRateTriggerFpm;
      const over = rates.filter((rate) => rate >= threshold).length;
      const highest = Math.max(...rates);
      return `${trendCylinderLabel(cylinder, rows)} cooled faster than ${threshold} F/min in ${over || rates.length} flagged flight${(over || rates.length) === 1 ? "" : "s"}; the highest detected rate was about ${Math.round(highest)} F/min.`;
    }
    if (signal.key === "turboScore") {
      const reason = trendTurboPrimaryReason(signal);
      const peaks = trendTurboPeakSummary(rows);
      return `Specifically, ${reason}${peaks ? `; flagged-flight peaks include ${peaks}` : ""}.`;
    }
    if (signal.key === "oilSystemScore") {
      const issueRows = rows
        .filter((row) => row.oilSystemIssueLabel || Number.isFinite(row.oilSystemIssueValue) || Number.isFinite(row.oilSystemMin) || Number.isFinite(row.oilSystemMax))
        .sort((a, b) => (a.oilSystemScore ?? 100) - (b.oilSystemScore ?? 100));
      const row = issueRows[0];
      if (!row) return "";
      const channel = row.oilSystemChannelLabel ? `${row.oilSystemChannelLabel}: ` : "";
      const detail = row.oilSystemIssueDetail
        || (Number.isFinite(row.oilSystemMin) ? `min ${Math.round(row.oilSystemMin)}` : "")
        || (Number.isFinite(row.oilSystemMax) ? `max ${Math.round(row.oilSystemMax)}` : "");
      const issue = row.oilSystemIssueLabel ?? "oil-system excursion";
      const windows = row.oilSystemWindowCount ? ` across ${row.oilSystemWindowCount} marked window${row.oilSystemWindowCount === 1 ? "" : "s"}` : "";
      return `${channel}${issue}${detail ? ` (${detail})` : ""}${windows}.`;
    }
    if (signal.key === "coolingScore" || signal.key === "coolingRateScore" || signal.key === "electricalScore") {
      const divergenceRows = trendDivergencePrimaryRows(signal);
      const repeated = trendDivergenceRepeatedCylinders(divergenceRows)[0];
      const lift = trendDivergenceLiftSummary(divergenceRows);
      if (!divergenceRows.length) return "";
      return `Specifically, ${repeated ? `${trendCylinderLabel(repeated.cylinder, divergenceRows)} repeats in ${repeated.count} flagged flight${repeated.count === 1 ? "" : "s"}` : "the split repeats across flagged flights"}${lift ? `; ${lift}` : ""}.`;
    }
    return "";
  }

  function trendSignalTitle(signal) {
    return `${signal.title}${signal.affected ? ` on ${signal.affected}` : ""}`;
  }

  function trendSignalNoun(signal) {
    const base = String(signal.title ?? "")
      .replace(/\s+needs\s+(review|watch|attention)\b/i, "")
      .replace(/\s+is\s+repeating\b/i, "");
    return `${base}${signal.affected ? ` on ${signal.affected}` : ""}`;
  }

  function trendSignalExplanationItems(signal, limit = 4) {
    const specific = signal.specificChecks?.(signal) ?? [];
    return [...specific, ...(signal.checks ?? [])].slice(0, limit);
  }

  function trendSignalContextItems(signal, limit = 6) {
    const specific = signal.specificContexts?.(signal) ?? [];
    return [...specific, ...(signal.contexts ?? [])].slice(0, limit);
  }

  function ownerTrendCopy(value) {
    return String(value ?? "")
      .replace(/\bThis trend\b/g, "This area")
      .replace(/\bthis trend\b/g, "this area")
      .replace(/\breview cue\b/g, "item to review")
      .replace(/\bcue\b/g, "item")
      .replace(/\bcues\b/g, "items")
      .replace(/\bscreening\b/g, "review")
      .replace(/\bthresholds\b/g, "settings");
  }

  function trendMeaningLeadText(signal) {
    const source = signal.meaning ?? "";
    const normalized = ownerTrendCopy(source).replace(/\s+/g, " ").trim();
    if (!normalized) return "";
    return normalized.match(/^(.+?[.!?])(?:\s|$)/)?.[1] ?? normalized;
  }

  function trendStoryBodyText(signal, direction) {
    if (signal.key === "egtScore") {
      const repeatSentence = `${trendSignalRepeatText(signal)} showed an EGT imbalance, and it is ${direction}.`;
      const evidenceSentence = trendSpecificEvidenceText(signal) || "EGT balance compares exhaust gas temperatures across cylinders during similar, stable phases of flight, so use this as a prompt to compare matching power and mixture conditions before drawing conclusions.";
      return [repeatSentence, evidenceSentence].filter(Boolean).join(" ");
    }
    if (signal.key === "combustionScore") {
      const repeatSentence = `${trendSignalRepeatText(signal)} showed a CHT/EGT pairing pattern, and it is ${direction}.`;
      const evidenceSentence = trendSpecificEvidenceText(signal) || "CHT/EGT pairing compares how a cylinder's head temperature and exhaust gas temperature move together, so use this as a prompt to compare the same cylinder, phase, power, and mixture context before drawing conclusions.";
      return [repeatSentence, evidenceSentence].filter(Boolean).join(" ");
    }
    const repeatSentence = `${trendSignalRepeatText(signal)} showed this ${signal.label.toLowerCase()} item, and it is ${direction}.`;
    const evidenceSentence = trendSpecificEvidenceText(signal) || "Use this as a prompt to compare similar phases of flight before drawing conclusions.";
    if (signal.key === "coolingScore" || signal.key === "coolingRateScore" || signal.key === "electricalScore") {
      return [
        trendMeaningLeadText(signal),
        repeatSentence,
        evidenceSentence,
      ].filter(Boolean).join("\n\n");
    }
    return [repeatSentence, evidenceSentence].filter(Boolean).join(" ");
  }

  function trendStableStoryBodyText(signal) {
    if (signal.key === "chtScore") {
      return `Recent flights do not show a repeating CHT heat-exposure item. No cylinder is repeatedly crossing the CHT review pattern this card watches: sustained time above ${Math.round(chtHealthReviewThresholdF)} F, caution-range time at or above ${Math.round(chtCautionThresholdF)} F, or unusually high peaks. Keep adding normal flights so future heat changes stand out.`;
    }
    if (signal.key === "egtScore") {
      return "Exhaust gas temperatures are balanced across cylinders. Recent flights do not show a repeating EGT imbalance, so keep adding normal flights to make future changes stand out.";
    }
    if (signal.key === "carbIceScore") {
      return "No repeating carb-ice pattern was detected in recent flights. Keep adding normal flights so any future carb-temperature, OAT, or power-loss changes stand out.";
    }
    if (signal.key === "chargingScore") {
      return "The electrical system appears to recover normally after engine start. Recent flights do not show a repeating weak-charge pattern, so keep adding normal starts to make future charging changes stand out.";
    }
    return `Recent flights do not show a repeating ${signal.label.toLowerCase()} item. Keep adding normal flights so future changes stand out.`;
  }

  function trendStableEmptyBodyText(story) {
    if (story.explanationSignal?.key === "chtScore") {
      return `Recent flights do not show a repeating CHT heat-exposure item. No cylinder is repeatedly crossing the CHT review or caution thresholds.`;
    }
    return story.queueMode === "selected"
      ? `Recent flights do not show a repeating ${String(story.queueMetricLabel ?? "selected area").toLowerCase()} item.`
      : "Recent flights do not show a repeated issue in the selected aircraft history.";
  }

  function trendImprovedStoryBodyText(signal) {
    const repeat = trendSignalRepeatText(signal);
    const label = signal.label.toLowerCase();
    const scoreClause = Number.isFinite(signal.latestScore)
      ? ` at ${Math.round(signal.latestScore)}/100`
      : "";
    if (signal.key === "electricalScore") {
      return `${repeat} previously showed bus-voltage or charging behavior that was less steady than expected. The newest flight scored back in the good range${scoreClause} — confirm on the next few similar flights before treating this as resolved.`;
    }
    return `${repeat} previously showed this ${label} issue. The newest flight is back in the good range${scoreClause} — watch the next few comparable flights to confirm the improvement holds.`;
  }

  function trendExplanationHtml(signal) {
    const checks = trendSignalExplanationItems(signal);
    const contexts = trendSignalContextItems(signal);
    const meaning = signal.specificMeaning?.(signal) ?? signal.meaning ?? "This is a repeated item in the selected aircraft history.";
    const why = signal.specificWhy?.(signal) ?? signal.why ?? "Repeated items are more useful than one-off flights because they show whether something persists across the aircraft history.";
    return `
      <section class="trend-explanation">
        <div>
          <span>What this means</span>
          <p>${escapeHtml(ownerTrendCopy(meaning))}</p>
        </div>
        <div>
          <span>Why it matters</span>
          <p>${escapeHtml(ownerTrendCopy(why))}</p>
        </div>
        ${contexts.length ? `
          <div>
            <span>Common explanations</span>
            <ul>${contexts.map((item) => `<li>${escapeHtml(ownerTrendCopy(item))}</li>`).join("")}</ul>
          </div>
        ` : ""}
        ${checks.length ? `
          <div>
            <span>What to check</span>
            <ul>${checks.map((item) => `<li>${escapeHtml(ownerTrendCopy(item))}</li>`).join("")}</ul>
          </div>
        ` : ""}
      </section>
    `;
  }

  function trendStoryForSignal(base, signal, options = {}) {
    if (signal.status.className === "muted") {
      return { ...base, className: "muted", status: "No data", title: `${signal.label}: latest score unavailable`, body: "Earlier scores remain in the history. The latest flight does not have a usable score for this area.", action: "", explanationSignal: signal };
    }
    const direction = trendDirectionText(signal.direction);
    const isSelected = options.selected;
    if (signal.status.className === "green" && !signal.recovered) {
      return {
        ...base,
        action: `Keep this area in the history and watch for future changes.`,
        body: trendStableStoryBodyText(signal),
        className: "green",
        explanationSignal: signal,
        status: "Stable",
        title: `${signal.label} is stable`,
      };
    }
    if (signal.recovered && signal.status.className === "green") {
      return {
        ...base,
        action: `Keep watching the next few comparable flights to confirm the improvement holds.`,
        body: trendImprovedStoryBodyText(signal),
        className: "green",
        explanationSignal: signal,
        status: "Stable",
        title: `${trendSignalNoun(signal)} has improved`,
      };
    }
    const storyStatus = signal.status.className === "red" ? "Attention" : "Watch";
    return {
      ...base,
      action: ownerTrendCopy(signal.action),
      body: trendStoryBodyText(signal, direction),
      className: storyStatus === "Attention" ? "red" : "amber",
      explanationSignal: signal,
      status: storyStatus,
      title: `${trendSignalTitle(signal)}${isSelected ? "" : ""}`,
    };
  }

  function buildTrendOwnerStoryContent(allRows, usableRows, aircraft) {
    const activeMetric = getActiveTrendMetric();
    const signals = buildTrendSignalSummaries(usableRows);
    const selectedWindow = usableRows.length;
    const coverage = trendCoverageLabel(allRows, usableRows);
    const selectedMetricMode = activeMetric !== "overallScore";
    const base = {
      action: "Keep uploading representative flights so the aircraft history gets more useful.",
      aircraft,
      body: "Use this page to spot repeated changes across flights before deciding what deserves a closer look.",
      className: "muted",
      coverage,
      queue: selectedMetricMode ? [] : signals.slice(0, 5),
      queueMode: selectedMetricMode ? "selected" : "aircraft",
      queueMetricLabel: trendMetricMeta[activeMetric]?.label ?? "Selected metric",
      status: "Limited",
      title: "Trend history is not ready yet",
      windowLabel: usableRows.length ? `Selected ${selectedWindow} flight${selectedWindow === 1 ? "" : "s"}` : "Selected history",
    };
    if (!usableRows.length) {
      return {
        ...base,
        body: allRows.length
          ? "The selected aircraft has flights listed, but none are ready for trend review yet."
          : "Upload or select engine-monitor flights to build an aircraft history.",
        action: "Add engine-monitor flights for this aircraft.",
      };
    }
    if (usableRows.length === 1) {
      return {
        ...base,
        body: `One flight is available for ${aircraft || "this aircraft"}. Trends become useful after several comparable flights.`,
        action: "Add more flights before treating patterns as meaningful.",
      };
    }
    const selected = selectedTrendSignal(usableRows);
    if (selected) {
      const selectedStory = trendStoryForSignal(base, selected, { selected: true });
      const selectedHasQueueItem = selected.flaggedCount > 0 || (Number.isFinite(selected.latestScore) && selected.latestScore < 88);
      return {
        ...selectedStory,
        queue: selectedHasQueueItem ? [selected] : [],
        queueMetricLabel: selected.label,
      };
    }
    const top = signals[0];
    if (!top) {
      return {
        ...base,
        className: "green",
        status: "Stable",
        title: "Running well — no repeating issues in this history",
        body: `The selected ${selectedWindow} flight${selectedWindow === 1 ? " stayed" : "s stayed"} consistent in the areas this page tracks. A clear trend page is a real finding, not just the absence of one — it means the tracked areas keep landing in their normal range.`,
        action: "Keep adding normal flights so future changes are easier to spot.",
      };
    }
    // Aggregate-first verdict (alarm redesign): the hero band describes the whole
    // history; the top signal rides below it as a single exception card instead of
    // becoming the page headline. One condition, one annunciation.
    const topStory = trendStoryForSignal(base, top);
    // Count tracked areas the same way the Status mix panel does — one entry per
    // scored signal tile, bucketed by status class — so the ribbon headline and the
    // mix takeaway never report different totals (they used to: 13 defs vs 12 tiles).
    const trackedSignals = buildAllTrendSignals(usableRows);
    const totalAreas = trackedSignals.length;
    const steadyAreas = trackedSignals.filter((signal) => signal.status?.className === "green").length;
    const reviewCount = totalAreas - steadyAreas;
    const attentionCount = trackedSignals.filter((signal) => signal.status?.className === "red").length;
    const hasAttention = attentionCount > 0;
    if (!reviewCount) {
      // Every flagged-history signal reads green (recovered) — the aggregate is healthy.
      return {
        ...topStory,
        title: "Running well — earlier cues have settled",
        body: `The selected ${selectedWindow} flights look consistent in the areas this page tracks; areas that flagged earlier scored back in the normal range on the newest flights.`,
      };
    }
    const exceptionPhrase = reviewCount === 1
      ? (hasAttention ? "1 area needs attention" : "1 pattern worth watching")
      : (hasAttention ? `${reviewCount} areas to look at, ${attentionCount} needing attention` : `${reviewCount} patterns worth watching`);
    return {
      ...topStory,
      title: hasAttention
        ? `${steadyAreas} of ${totalAreas} tracked areas are steady — ${exceptionPhrase}`
        : `Running well overall — ${steadyAreas} of ${totalAreas} tracked areas steady, ${exceptionPhrase}`,
      body: `Across the selected ${selectedWindow} flights, most tracked areas stayed in their normal range. The strongest exception is called out below — it is ${trendDirectionText(top.direction)}.`,
      // The exception card carries the specific next step; the band-level action
      // stays empty so the same instruction is not annunciated twice.
      action: "",
      exception: {
        action: topStory.action,
        body: topStory.body,
        className: topStory.className,
        label: top.label,
        status: topStory.status,
        title: trendSignalTitle(top),
      },
    };
  }

  function trendQueueNote(signal) {
    const repeat = signal.recentFlagged
      ? `${signal.recentFlagged} of ${signal.recentCount} selected flights`
      : `${signal.flaggedCount} selected flight${signal.flaggedCount === 1 ? "" : "s"}`;
    const affected = signal.affected ? ` · ${signal.affected}` : "";
    const consecutive = signal.consecutive >= 2 ? " · repeated recently" : "";
    return `${repeat}${affected}${consecutive} · ${trendDirectionText(signal.direction)}`;
  }

  function buildTrendOwnerStory(allRows, usableRows, aircraft) {
    const story = buildTrendOwnerStoryContent(allRows, usableRows, aircraft);
    if (getActiveTrendMetric() !== "overallScore" || usableRows.length < 2) return story;
    const verdict = overallTrendVerdict(usableRows, buildAllTrendSignals(usableRows));
    if (story.className === verdict.className) return { ...story, status: verdict.label };
    return {
      ...story,
      status: verdict.label,
      className: verdict.className,
      title: verdict.className === "muted" ? "Latest overall score is unavailable" : `Overall trend: ${verdict.label.toLowerCase()}`,
      body: "The overall verdict includes the overall score history and the tracked areas. Review the contributing flight checks for context.",
      action: "",
    };
  }

  return {
    trendHealthKeyForSignalKey,
    trendReviewMonitorEnabledForSignalKey,
    trendSignalDefinitionsForRows,
    trendDominantAffected,
    trendAverageScore,
    trendConsecutiveFlagCount,
    trendDirection,
    trendDirectionText,
    trendSignalStatus,
    buildTrendSignalSummary,
    buildAllTrendSignals,
    buildTrendSignalSummaries,
    trendCoverageLabel,
    selectedTrendSignal,
    trendStoryMetricKey,
    trendSignalRepeatText,
    trendAffectedRows,
    trendDominantNumber,
    trendCylinderPeakCht,
    trendChtThresholdSummary,
    trendValveSpecificEvidenceText,
    trendSpecificEvidenceText,
    trendSignalTitle,
    trendSignalNoun,
    trendSignalExplanationItems,
    trendSignalContextItems,
    ownerTrendCopy,
    trendMeaningLeadText,
    trendStoryBodyText,
    trendStableStoryBodyText,
    trendStableEmptyBodyText,
    trendImprovedStoryBodyText,
    trendExplanationHtml,
    trendStoryForSignal,
    buildTrendOwnerStory,
    trendQueueNote,
  };
}
